import React, { useState, useEffect, useRef, useCallback, forwardRef } from 'react'
import ReactDOM from 'react-dom'
import { concatStyles, getLabel } from 'src/utils'
import { LabelOption } from 'src/@types'
import { useStyles } from './styles'
import { Loading } from 'src/components/Loading'

interface FetchProps {
  onFetchMore?: () => void
  hasNextPage?: boolean
  isFetching?: boolean
  onSearchChange?: (searchValue: string) => void
  fetchById?: (id: string) => Promise<any | null>
}

interface SelectInputWithFetchProps {
  label: string
  value: string | number
  options: LabelOption[]
  selectBy: string
  showBy: string[] | any
  placeholder?: string
  onChange: (item: LabelOption | null) => void
  error?: string
  className?: string
  disabled?: boolean
  id?: string
  onLoad?: Function
  fetchProps?: FetchProps
  renderOption?: (option: any) => React.ReactNode
}

const SelectFetchInputComponent = forwardRef<HTMLInputElement, SelectInputWithFetchProps>(
  (
    {
      options = [],
      value,
      label,
      selectBy,
      showBy,
      placeholder,
      onChange,
      error,
      className,
      onLoad,
      fetchProps = {},
      renderOption,
      ...rest
    },
    ref
  ) => {
    const { onFetchMore, hasNextPage = false, isFetching = false, onSearchChange, fetchById } = fetchProps

    const [inputValue, setInputValue] = useState('')
    const [searchValue, setSearchValue] = useState('')
    const [focused, setFocused] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const [isTyping, setIsTyping] = useState(false)

    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
    const [isLoadingMore, setIsLoadingMore] = useState(false)

    const listRef = useRef<HTMLUListElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLUListElement | null>(null)
    const observerRef = useRef<IntersectionObserver | null>(null)
    const loadingRef = useRef<HTMLLIElement>(null)
    const fetchedRef = useRef<string | number | null>(null)
    const itemRefs = useRef<(HTMLLIElement | null)[]>([])

    const classes = useStyles()

    // Callback ref para atribuir a ambos os refs
    const setDropdownRef = useCallback((el: HTMLUListElement | null) => {
      ;(listRef as React.MutableRefObject<HTMLUListElement | null>).current = el
      dropdownRef.current = el
    }, [])

    // =========================
    // POSITION
    // =========================
    const updatePosition = useCallback(() => {
      if (!wrapperRef.current) return
      const rect = wrapperRef.current.getBoundingClientRect()

      setDropdownStyle({
        position: 'absolute',
        top: rect.bottom + window.scrollY - 6,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 1600
      })
    }, [])

    useEffect(() => {
      if (focused) updatePosition()
    }, [focused, updatePosition])

    useEffect(() => {
      if (!focused) return
      window.addEventListener('scroll', updatePosition)
      window.addEventListener('resize', updatePosition)
      return () => {
        window.removeEventListener('scroll', updatePosition)
        window.removeEventListener('resize', updatePosition)
      }
    }, [focused, updatePosition])

    // =========================
    // CLICK OUTSIDE
    // =========================
    useEffect(() => {
      const handleClickOutside = (e: any) => {
        const isOutsideWrapper = wrapperRef.current && !wrapperRef.current.contains(e.target)
        const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(e.target)

        if (isOutsideWrapper && isOutsideDropdown) {
          setFocused(false)
          setIsTyping(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('touchstart', handleClickOutside)
      }
    }, [])

    // =========================
    // SEARCH (REMOTE)
    // =========================
    useEffect(() => {
      onSearchChange?.(searchValue)
    }, [searchValue, onSearchChange])

    // =========================
    // VALUE SYNC (corrigido)
    // =========================
    useEffect(() => {
      // 👇 não sobrescreve enquanto digita
      if (isTyping) return

      const selected = options.find((item) => item[selectBy] === value)

      if (selected) {
        const label = getLabel(selected, showBy)
        setInputValue(label)
        setSearchValue('')
        onLoad?.(selected)
      } else if (!value) {
        setInputValue('')
        setSearchValue('')
      }
    }, [value, options, isTyping])

    // =========================
    // FETCH BY ID
    // =========================
    useEffect(() => {
      if (!value || !fetchById) return

      const exists = options.some((item) => item[selectBy] === value)

      if (!exists && fetchedRef.current !== value) {
        fetchedRef.current = value

        fetchById(value as string).then((item) => {
          if (item) {
            setInputValue(getLabel(item, showBy))
          }
        })
      }
    }, [value, options, fetchById, selectBy, showBy])

    // =========================
    // INFINITE SCROLL
    // =========================
    useEffect(() => {
      if (!loadingRef.current) return

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetching && !isLoadingMore) {
          setIsLoadingMore(true)
          onFetchMore?.()
        }
      })

      observerRef.current.observe(loadingRef.current)

      return () => observerRef.current?.disconnect()
    }, [hasNextPage, isFetching, isLoadingMore, onFetchMore])

    useEffect(() => {
      if (!isFetching) setIsLoadingMore(false)
    }, [isFetching])

    // =========================
    // SCROLL COM SETA
    // =========================
    useEffect(() => {
      if (highlightedIndex < 0) return

      const el = itemRefs.current[highlightedIndex]
      const container = listRef.current

      if (el && container) {
        const elTop = el.offsetTop
        const elBottom = elTop + el.offsetHeight

        const containerTop = container.scrollTop
        const containerBottom = containerTop + container.clientHeight

        if (elTop < containerTop) {
          container.scrollTop = elTop
        } else if (elBottom > containerBottom) {
          container.scrollTop = elBottom - container.clientHeight
        }
      }
    }, [highlightedIndex])

    // =========================
    // HANDLERS
    // =========================
    const handleChangeInput = (value: string) => {
      setIsTyping(true)
      setInputValue(value)
      setSearchValue(value)
      setHighlightedIndex(-1)

      if (!focused) setFocused(true)
    }

    const handleClick = (option: LabelOption) => {
      const label = getLabel(option, showBy)

      setIsTyping(false)
      setInputValue(label)
      setSearchValue('')
      onChange(option)
      setFocused(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex((prev) => Math.min(prev + 1, options.length - 1))
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex((prev) => Math.max(prev - 1, 0))
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        if (highlightedIndex >= 0) {
          handleClick(options[highlightedIndex])
        }
      }
    }

    // =========================
    // RENDER
    // =========================
    return (
      <div className={concatStyles(classes.filterButtonWrapper, className)}>
        <label className='select-input__label'>{label}</label>

        <div ref={wrapperRef}>
          <input
            ref={ref}
            value={inputValue}
            placeholder={placeholder}
            className={`select-input__input ${error ? 'select-input__input--error' : ''}`}
            onClick={() => {
              setFocused(true)
              setHighlightedIndex(-1)
              setSearchValue('')
            }}
            onBlur={() => {
              setFocused(false)
              setSearchValue('')
              setIsTyping(false)

              const match = options.find((item) => getLabel(item, showBy).toLowerCase() === inputValue.toLowerCase())

              if (!match) {
                setInputValue('')
                onChange(null)
              } else if (match[selectBy] !== value) {
                setInputValue(getLabel(match, showBy))
                onChange(match)
              }
            }}
            onChange={(e) => handleChangeInput(e.target.value)}
            onKeyDown={handleKeyDown}
            {...rest}
          />

          {error && <span className='select-input__error'>{error}</span>}

          {focused &&
            ReactDOM.createPortal(
              <ul ref={setDropdownRef} className={classes.selectInputDropper} style={dropdownStyle}>
                {isFetching ? (
                  <li className={classes.loadingItem}>
                    <Loading />
                  </li>
                ) : options.length > 0 ? (
                  <>
                    {options.map((item, index) => (
                      <li
                        key={item[selectBy]}
                        ref={(el) => (itemRefs.current[index] = el)}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          handleClick(item)
                        }}
                        onTouchStart={(e) => {
                          const touch = e.touches[0]
                          e.currentTarget.dataset.touchStartY = String(touch.clientY)
                        }}
                        onTouchEnd={(e) => {
                          const touch = e.changedTouches[0]
                          const startY = Number(e.currentTarget.dataset.touchStartY || 0)

                          if (Math.abs(touch.clientY - startY) < 10) {
                            handleClick(item)
                          }
                        }}
                        className={highlightedIndex === index ? 'highlighted' : ''}
                      >
                        {renderOption ? renderOption(item) : getLabel(item, showBy)}
                      </li>
                    ))}

                    {hasNextPage && <li ref={loadingRef} style={{ height: 1 }} />}

                    {isLoadingMore && (
                      <li className={classes.loadingItem}>
                        <Loading />
                      </li>
                    )}
                  </>
                ) : (
                  <li>Nenhum registro encontrado.</li>
                )}
              </ul>,
              document.body
            )}
        </div>
      </div>
    )
  }
)

SelectFetchInputComponent.displayName = 'SelectFetchInput'
export const SelectFetchInput = SelectFetchInputComponent
